import React, { useState, useEffect, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useLocation } from 'react-router-dom';
import { CmsConfigs } from './models/configs.model';
import { HolderModel } from './models/holder.model';
import { HolderService, HOLDER_STATUS_VERIFY_EMAIL, HOLDER_STATUS_NOT_ENTITLED } from './services/holder.service';
import { IdentityService, IDENTITY_ERROR_ALREADY_VERIFIED, IDENTITY_ERROR_INVALID_EMAIL, IDENTITY_ERROR_INVALID_TOKEN, IDENTITY_ERROR_GENERIC } from './services/identity.service';
import { getConfigs, getHomepath } from './util/cms-utils';
import * as CSS from 'csstype';
import { FullLoader } from './components/loader.component';

interface OwnProps {
  holderHost: string;
  identityHost: string;
}

const AppController = ({ holderHost, identityHost }: OwnProps) => {
  const { authState, oktaAuth } = useOktaAuth()
  const identityService: IdentityService = new IdentityService(authState, identityHost);
  const [message, setMessage] = useState<string>();
  const [showEmailButton, setShowEmailButton] = useState<Boolean>(false);
  const [email, setEmail] = useState<string>("");
  const { search } = useLocation()
  const [config, setConfig] = useState<CmsConfigs>();

  const init = useCallback(async (config: CmsConfigs) => {
    if (config) {
        setConfig(config);
        try {
          const holderService = new HolderService(authState, holderHost);
          const result: HolderModel = await holderService.getValidationStatus(config.clubCode, config.venueId);
          console.log(JSON.stringify(result));
          if (!result || !result.accountId) {
            if (config.clubCode === 'min') {
              // twins fans can't create backend account on our site, no entitlement
              window.parent.location.href = `${getHomepath(config)}/message/not-entitled`;
            } else {
              window.parent.location.href = `${getHomepath(config)}/sign-up`;
            }
          } else {
            window.parent.location.href = `${getHomepath(config)}/dashboard`;
          }
        } catch (_error) {
          const error = _error as Error
          console.error(error);
          if (error.message === HOLDER_STATUS_NOT_ENTITLED) {
            if (config.clubCode === 'chc')
              window.parent.location.href = `${getHomepath(config)}/sign-up`;
            else
              window.parent.location.href = `${getHomepath(config)}/message/not-entitled`;
          } else if (error.message === HOLDER_STATUS_VERIFY_EMAIL) {
            setMessage(error.message);
            setShowEmailButton(true);
          } else {
            setMessage(error.message);
          }
        }
      } else {
        console.error("config object not found");
      }
  }, [authState, holderHost]);

  useEffect(() => {
    const config: CmsConfigs = getConfigs(new URLSearchParams(search).get("clubCode"));
    const isAuthenticated = authState?.isAuthenticated
      if (isAuthenticated) {
        oktaAuth.getUser().then(userInfo => {
          userInfo.email && setEmail(userInfo.email);
          init(config);
        });
      }

  }, [authState?.isAuthenticated, oktaAuth, init, search]);

  const handleSendVerifyEmailClick: () => void = async (): Promise<void> => {
    try {
      const result: string = await identityService.sendEmailVerification(email);
      if (result === "ok") {
        console.log('email verification send');
        setShowEmailButton(false);
      }
    } catch (error) {
      console.error(error);
      setShowEmailButton(false);
    }
  };

  function handleParentRedirectSTHApp() {
    window.parent.location.href = `${getHomepath(config)}/app`;
  }

  function showVerifyEmail() {
    return (
      <div>
        <p style={messageStyle}>In order to proceed please find the MLB verification email in your inbox and confirm your email address.</p>
        <p style={messageStyle}>Not seeing the email? Use the button below to have it resent.</p>
        {showEmailButton ?
          <button name='verifyEmail' onClick={handleSendVerifyEmailClick} >Resend Verification Email</button>
          :
          <button name='verifyEmail' onClick={handleSendVerifyEmailClick} disabled >Resend Verification Email</button>
        }
        <p style={messageStyle}>After verification is complete, <button name='goHome' onClick={handleParentRedirectSTHApp} style={buttonAsLink}>reload</button> this page.</p>
      </div>
    )
  }

  function showMessage() {
    console.debug(message + ' ' + config);
    if (message === HOLDER_STATUS_VERIFY_EMAIL) {
      return showVerifyEmail()
    } else if (message === IDENTITY_ERROR_INVALID_EMAIL) {
      return <p>Your email address was not valid.</p>
    } else if (message === IDENTITY_ERROR_ALREADY_VERIFIED) {
      return <p>Your email address was already validated. </p>
    } else if (message === IDENTITY_ERROR_INVALID_TOKEN) {
      return <p>Your session is not valid.</p>
    } else if (message === IDENTITY_ERROR_GENERIC) {
      return <p>There was a problem encountered while sending a verification email.</p>
    }
    else {
      return <FullLoader />
    }
  }

  return (
    <div style={{ margin: '20px' }}>
      {showMessage()}
    </div>
  )
};

const messageStyle: CSS.Properties = {
  fontFamily: "Helvetica Neue,Helvetica,Arial,sans-serif",
  fontSize: "14px"
}

const buttonAsLink: CSS.Properties = {
  background: "none!important",
  border: "none",
  padding: "0!important",
  color: "#069",
  textDecoration: "underline",
  cursor: "pointer",
  fontFamily: "Helvetica Neue,Helvetica,Arial,sans-serif",
  fontSize: "14px"
}

export default AppController;
